import { useCallback } from 'react';
import {
  PermanentConfirmStrategy, useConfirmationModal,
} from '~/_shared/components/modal/confirmation/useConfirmationModal';
import { useTranslation } from '~/_shared/utils/hooks';
import { useModal } from '~/modal/useModal.hook';
import { useBoundaryFormMode } from '~/store/frontendState/mapTools/boundary/boundaryForm/boundaryForm.selectors';
import {
  useOptimizedTerritoriesEnabledSelector, useOptimizedWMSTerritoriesEnabledSelector,
} from '~/store/userData/userData.selectors';
import { CreateCustomBoundaryMode } from '../createCustomBoundaryGroupPane.component';

export enum OptimizedTerritoriesService {
  WMS = 'WMS',
  Maptive = 'Maptive',
}

export const useCreateOptimizedTerritoriesWithSubscriptionCheck = () => {
  const { openConfirmationModal, closeConfirmationModal } = useConfirmationModal();
  const optimizedWMSTerritoriesEnabled = useOptimizedWMSTerritoriesEnabledSelector();
  const optimizedTerritoriesEnabled = useOptimizedTerritoriesEnabledSelector();
  const [t] = useTranslation();
  const { openModal: openWMSModal } = useModal('CreateBoundaryGroupFromAI');
  const { openModal: openCreateOptimizedTerritoriesModal } = useModal('CreateOptimizedTerritories');
  const mode = useBoundaryFormMode();

  const defaultService = mode === CreateCustomBoundaryMode.WMSOptimized
    ? OptimizedTerritoriesService.WMS
    : OptimizedTerritoriesService.Maptive;

  const openModal = useCallback((service: OptimizedTerritoriesService) => {
    if (service === OptimizedTerritoriesService.Maptive) {
      openCreateOptimizedTerritoriesModal({ });
    }
    else {
      openWMSModal({ });
    }
  }, [openWMSModal, openCreateOptimizedTerritoriesModal]);

  const openOptimizedTerritoriesModal = useCallback((service = defaultService) => {
    const optimizedWMSTerritoriesAccepted = service === OptimizedTerritoriesService.WMS && optimizedWMSTerritoriesEnabled;
    const optimizedTerritoriesAccepted = service === OptimizedTerritoriesService.Maptive && optimizedTerritoriesEnabled;

    if (optimizedWMSTerritoriesAccepted || optimizedTerritoriesAccepted) {
      openModal(service);
      return;
    }

    openConfirmationModal({
      title: t('Auto Territory Builder - trial'),
      text: (
        <div>
          {t('Auto Territory Builder requires a subscription which your account does not have active. You can try this tool with trial limitations:')}

          <ul>
            <li>{t('Generated territories are not permanent')}</li>
            <li>{t('Data export is disabled')}</li>
          </ul>
        </div>
      ),
      confirmCaption: t('Continue'),
      onConfirm: () => {
        closeConfirmationModal();
        openModal(service);
      },
      permanentConfirmSettings: {
        id: 'OPTIMIZED_TERRITORIES_TRIAL',
        strategy: PermanentConfirmStrategy.Session,
      },
    });
  }, [defaultService, optimizedWMSTerritoriesEnabled, optimizedTerritoriesEnabled, openConfirmationModal, t, openModal, closeConfirmationModal]);

  return {
    openOptimizedTerritoriesModal,
  };
};
